import {
  ComponentProps,
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import { Data } from '@pcmp/react-coworker-components';
import SSRIcon from '@ingka/ssr-icon';
import styles from './index.module.css';

const { PillDropdown } = Data;

const isOpenContext = createContext<
  [boolean, Dispatch<SetStateAction<boolean>>]
>(null as any);

const useIsOpen = () => useContext(isOpenContext);

const IsOpenProvider: FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const state = useState(false);

  return (
    <isOpenContext.Provider value={state}>{children}</isOpenContext.Provider>
  );
};

export const PillDropdownButton: FC<{
  icon?: any;
  text: ReactNode;
  onClick?: () => void;
}> = ({ text, icon, onClick }) => {
  const [, setOpen] = useIsOpen();

  return (
    <div className="flex justify-center">
      <div
        role="button"
        className="w-full px-6 py-2 self-center cursor-pointer hover:bg-skapa-interactive-secondary-bg-default"
        onClick={() => {
          setOpen(false);
          onClick?.();
        }}
      >
        {icon && <SSRIcon paths={icon} />}

        {text}
      </div>
    </div>
  );
};

const DropdownButtonContent: FC<
  {
    children: React.ReactNode;
    type: 'primary' | 'secondary' | 'tertiary';
    size?: 'small' | 'medium' | 'large';
  } & Omit<ComponentProps<typeof PillDropdown>, 'open' | 'onClick' | 'onClose'>
> = ({ children, type = 'primary', size = 'medium', ...rest }) => {
  const [isOpen, setOpen] = useIsOpen();

  return (
    <PillDropdown
      {...rest}
      className={`${styles['pill-dropdown']} ${
        styles[`pill-dropdown__${type}`]
      } ${styles[`pill-dropdown__${size}`]}`}
      open={isOpen!}
      onClick={() => setOpen(!isOpen)}
      onClose={() => setOpen(false)}
    >
      {children}
    </PillDropdown>
  );
};

export const DropdownButton: FC<
  ComponentProps<typeof DropdownButtonContent>
> = ({ children, ...props }) => {
  return (
    <IsOpenProvider>
      <DropdownButtonContent {...props}>{children}</DropdownButtonContent>
    </IsOpenProvider>
  );
};
